import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Re-usable "Previous Consultation Found" warning dialog.
 *
 * Props:
 *  - open            {boolean}
 *  - cancelling      {boolean}  – shows spinner on the proceed button
 *  - onDismiss       {Function} – "Go Back" = dismiss & proceed without cancelling
 *  - onCancelAndProceed {Function} – "Proceed & Cancel Previous"
 */
const FreeConsultationDialog = ({ open, cancelling, onDismiss, onCancelAndProceed }) => (
  <Dialog
    open={open}
    onClose={cancelling ? undefined : onDismiss}
    aria-labelledby="free-consultation-dialog-title"
    PaperProps={{
      sx: {
        borderRadius: '20px',
        maxWidth: '520px',
        mx: 2,
      },
    }}
  >
    <DialogTitle
      id="free-consultation-dialog-title"
      sx={{
        m: 0,
        p: 2,
        fontSize: '18px',
        fontWeight: 500,
        fontFamily: 'FiraSans',
        pr: 6,
      }}
    >
      Previous Consultation Found
    </DialogTitle>
    <IconButton
      aria-label="close"
      onClick={onDismiss}
      disabled={cancelling}
      sx={{
        position: 'absolute',
        right: 8,
        top: 8,
        color: (theme) => theme.palette.grey[500],
      }}
    >
      <CloseIcon />
    </IconButton>
    <DialogContent dividers>
      <Typography
        sx={{
          fontSize: '15px',
          fontWeight: 400,
          fontFamily: 'FiraSans',
          lineHeight: 1.6,
          color: '#333',
          py: 1,
        }}
      >
        It looks like you may have previously booked a consultation with us.
      </Typography>
      <Typography
        sx={{
          fontSize: '15px',
          fontWeight: 400,
          fontFamily: 'FiraSans',
          lineHeight: 1.6,
          color: '#333',
          py: 1,
        }}
      >
        If you would like to reconnect or reschedule with your current practitioner, please reach out
        to them directly through Nirva’s secure{' '}
        <strong>SimplePractice Client Portal</strong>.
      </Typography>
      <Typography
        sx={{
          fontSize: '15px',
          fontWeight: 400,
          fontFamily: 'FiraSans',
          lineHeight: 1.6,
          color: '#333',
          py: 1,
        }}
      >
        If you are seeking to book with a new practitioner, please proceed. Your previous
        consultation will be automatically cancelled and your practitioner will be notified.
      </Typography>
    </DialogContent>
    <DialogActions
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: 2,
        p: 2,
      }}
    >
      <Button
        variant="outlined"
        onClick={onDismiss}
        disabled={cancelling}
        sx={{
          height: '40px',
          textTransform: 'none',
          borderRadius: '8px',
          borderColor: '#492A18',
          color: '#492A18',
          fontSize: '14px',
          fontWeight: 500,
          fontFamily: 'FiraSans',
          px: 3,
          '&:hover': {
            borderColor: '#3a2113',
            backgroundColor: 'rgba(73, 42, 24, 0.04)',
          },
        }}
      >
        Go Back
      </Button>
      <Button
        variant="contained"
        onClick={onCancelAndProceed}
        disabled={cancelling}
        sx={{
          height: '40px',
          backgroundColor: '#492A18',
          textTransform: 'none',
          borderRadius: '8px',
          color: '#fff',
          fontSize: '14px',
          fontWeight: 500,
          fontFamily: 'FiraSans',
          px: 3,
          '&:hover': {
            backgroundColor: '#3a2113',
          },
        }}
      >
        {cancelling ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Proceed & Cancel Previous'}
      </Button>
    </DialogActions>
  </Dialog>
);

export default FreeConsultationDialog;
